import { memo, useEffect, useRef } from 'react'

import { load } from '@2gis/mapgl'

import {
  type LngLatBounds,
  type PolylineOptions,
  type Map,
  type MapOptions,
  type PolygonOptions,
} from '@2gis/mapgl/types'

type MapWrapperProps = {
  map: MapOptions
  viewportBounds?: LngLatBounds
  fitPolylineToView?: boolean
  setSelectedObjects?: string[]
  markerCords?: number[]
  markers?: number[][]
  polygon?: PolygonOptions
  polygons?: PolygonOptions[]
  polyline?: PolylineOptions
  polylineWithoutMarksSE?: boolean
}

const getPolylineBounds = (
  polyline: PolylineOptions,
): LngLatBounds | undefined => {
  const { coordinates } = polyline

  if (!coordinates.length) {
    return undefined
  }

  let west = Infinity
  let south = Infinity
  let east = -Infinity
  let north = -Infinity

  coordinates.forEach(([longitude, latitude]) => {
    west = Math.min(west, longitude)
    south = Math.min(south, latitude)
    east = Math.max(east, longitude)
    north = Math.max(north, latitude)
  })

  if (![west, south, east, north].every(Number.isFinite)) {
    return undefined
  }

  return {
    southWest: [west, south],
    northEast: [east, north],
  }
}

const mapglApiPromise = load()

const MapWrapper = ({
  map,
  viewportBounds,
  fitPolylineToView = false,
  markerCords,
  markers,
  setSelectedObjects,
  polygon,
  polygons,
  polyline,
  polylineWithoutMarksSE,
}: MapWrapperProps) => {
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let mapBox: Map | undefined
    let fitBoundsFrameId: number | undefined
    let fitBoundsObserver: ResizeObserver | undefined
    let isDisposed = false
    const mapContainer = containerRef.current

    if (!mapContainer) {
      return
    }

    mapglApiPromise.then(mapglAPI => {
      if (isDisposed) {
        return
      }

      const activeMap = new mapglAPI.Map(mapContainer, {
        ...map,
        lang: 'ru',
        pitch: map.pitch ?? ((map.zoom ?? map.styleZoom ?? 0) >= 15 ? 45 : 0),
        graphicsPreset: 'normal',
        disableAntiAliasing: false,
        disableHidingPois: false,
        enableTrackResize: true,
        styleState: {
          ...map.styleState,
          parkingOn: false,
          immersiveRoadsOn: true,
          terrainEnabled: true,
        },
        zoomControl: false,
        key: 'eb5baecb-c5db-461d-a79b-b920817fb6ad',
      })
      mapBox = activeMap

      const boundsToFit =
        viewportBounds ??
        (fitPolylineToView && polyline
          ? getPolylineBounds(polyline)
          : undefined)

      if (boundsToFit) {
        const fitMapToBounds = () => {
          const edgePadding = mapContainer.clientWidth <= 640 ? 20 : 32
          const isCompactViewport = window.innerWidth <= 1025

          activeMap.fitBounds(boundsToFit, {
            padding: {
              top: isCompactViewport ? 72 : edgePadding,
              right: edgePadding,
              bottom: isCompactViewport ? 80 : 72,
              left: edgePadding,
            },
            skipMapPadding: true,
            animation: {
              animate: false,
              duration: 0,
            },
          })
        }

        const scheduleFitMapToBounds = () => {
          if (fitBoundsFrameId !== undefined) {
            cancelAnimationFrame(fitBoundsFrameId)
          }

          fitBoundsFrameId = requestAnimationFrame(() => {
            fitBoundsFrameId = requestAnimationFrame(() => {
              fitBoundsFrameId = undefined

              if (!isDisposed) {
                fitMapToBounds()
              }
            })
          })
        }

        fitBoundsObserver = new ResizeObserver(scheduleFitMapToBounds)
        fitBoundsObserver.observe(mapContainer)
        scheduleFitMapToBounds()
      }

      activeMap.on('click', async event => {
        const coordinates = event.lngLat.map(coordinate =>
          Number(coordinate.toFixed(6)),
        )
        const coordinatesText = `[${coordinates.join(', ')}]`
        const zoom = Number(activeMap.getZoom().toFixed(2))
        const mapStateText = `${coordinatesText}\nzoom: ${zoom}`

        try {
          await navigator.clipboard.writeText(mapStateText)

          alert(
            `Координаты клика:\n${coordinatesText}\nZoom: ${zoom}\n\nСкопировано в буфер обмена`,
          )
        } catch {
          alert(
            `Координаты клика:\n${coordinatesText}\nZoom: ${zoom}\n\nНе удалось скопировать автоматически`,
          )
        }
      })

      if (setSelectedObjects?.length) {
        activeMap.setSelectedObjects(setSelectedObjects)
      }

      if (markerCords?.length) {
        new mapglAPI.Marker(activeMap, {
          coordinates: markerCords,
        })
      }

      if (markers?.length) {
        markers.forEach(marker => {
          new mapglAPI.Marker(activeMap, {
            coordinates: marker,
            icon: 'https://docs.2gis.com/img/mapgl/marker.svg',
          })
        })
      }

      if (polygon) {
        new mapglAPI.Polygon(activeMap, {
          ...polygon,
        })
      }

      polygons?.forEach(polygonOptions => {
        new mapglAPI.Polygon(activeMap, polygonOptions)
      })

      if (polyline) {
        new Array(2).fill(undefined).map((_, index) => {
          new mapglAPI.Polyline(activeMap, {
            ...polyline,
            width: index ? 8 : 12,
            color: index ? polyline.color || '#296BB9' : '#ffffff',
          })
        })

        if (!polylineWithoutMarksSE) {
          new mapglAPI.CircleMarker(activeMap, {
            coordinates: [polyline.coordinates[0]].flat(),
            radius: 20,
            color: '#296BB9',
            strokeWidth: 2,
            strokeColor: '#ffffff',
          })

          new mapglAPI.Label(activeMap, {
            coordinates: [polyline.coordinates[0]].flat(),
            text: 'A',
            fontSize: 14,
            color: '#ffffff',
          })

          new mapglAPI.CircleMarker(activeMap, {
            coordinates: [
              polyline.coordinates[polyline.coordinates.length - 1],
            ].flat(),
            radius: 20,
            color: '#296BB9',
            strokeWidth: 2,
            strokeColor: '#ffffff',
          })

          new mapglAPI.Label(activeMap, {
            coordinates: [
              polyline.coordinates[polyline.coordinates.length - 1],
            ].flat(),
            text: 'B',
            fontSize: 14,
            color: '#ffffff',
          })
        }
      }

      // mapBox.on('click', e => {
      //   if (!e.target) {
      //     return
      //   }
      //   const { id } = e.target
      //   alert('Идентификатор объекта: ' + id + ' ' + mapBox.getCenter())
      // })
    })

    return () => {
      isDisposed = true
      fitBoundsObserver?.disconnect()

      if (fitBoundsFrameId !== undefined) {
        cancelAnimationFrame(fitBoundsFrameId)
      }

      mapBox?.destroy()
    }
  }, [
    map,
    viewportBounds,
    fitPolylineToView,
    markerCords,
    polygon,
    polygons,
    setSelectedObjects,
    polyline,
    polylineWithoutMarksSE,
    markers,
  ])

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%' }}></div>
  )
}

export default memo(MapWrapper, () => true)
